import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Check, Loader2 } from "lucide-react";
import { useMe } from "@/lib/api";
import { useCheckout } from "@/lib/useCheckout";
import FirmPlans from "@/components/FirmPlans";

const FREE = ["1 export every 30 days", "All PDFs in a case, zipped", "Skip already-downloaded filings", "Crawl history"];
const PRO = ["Unlimited exports", "Everything in Free", "Large cases split into ZIP parts", "Cancel anytime"];

export const Feature = ({ children }) => (
  <li className="flex gap-2"><Check size={16} className="text-green-600 shrink-0 mt-0.5" />{children}</li>
);

export default function PricingCards() {
  const { isSignedIn } = useAuth();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const { checkout, busy, ready } = useCheckout();
  const isPro = me?.usage?.plan === "pro";
  const plans = [
    { id: "free", name: "Free", price: "$0", per: "", items: FREE, note: "No card needed" },
    { id: "monthly", name: "Pro Monthly", price: "$9.99", per: "/month", items: PRO, note: "Billed monthly" },
    { id: "yearly", name: "Pro Yearly", price: "$99.99", per: "/year", items: PRO, note: "Save about 17% vs monthly", best: true },
  ];
  const proLabel = (p) => {
    if (!isPro) return `Upgrade - ${p.price}${p.per}`;
    if (me?.usage?.source === "firm") return "Included with your team";
    return me?.subscription?.lookup_key === `pro_${p.id}` ? "Your current plan" : "Pro active";
  };

  return (
    <div className="space-y-14">
      <div className="grid md:grid-cols-3 gap-5" data-testid="pricing-cards">
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.id}`} className={`relative bg-white rounded-lg p-6 flex flex-col border ${p.best ? "border-blue-500 shadow-md" : "border-slate-200"}`}>
            {p.best && <span className="absolute -top-3 left-6 text-[11px] font-bold uppercase tracking-wider bg-blue-600 text-white px-2.5 py-1 rounded-full">Best value</span>}
            <h3 className="font-heading text-lg font-bold">{p.name}</h3>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-heading text-4xl font-extrabold tabular-nums">{p.price}</span>
              <span className="text-slate-500 text-sm">{p.per}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{p.note}</p>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-700 flex-1">{p.items.map((i) => <Feature key={i}>{i}</Feature>)}</ul>
            {p.id === "free" ? (
              <button data-testid="plan-free-btn" onClick={() => navigate(isSignedIn ? "/" : "/sign-up")} className="mt-6 h-11 rounded-md border border-slate-300 text-sm font-semibold hover:bg-slate-50 transition-colors">
                {isSignedIn ? (isPro ? "Included" : "Your current plan") : "Start free"}
              </button>
            ) : (
              <button
                data-testid={`plan-${p.id}-btn`}
                disabled={!!busy || isPro || !ready}
                onClick={() => checkout(p.id)}
                className="mt-6 h-11 rounded-md bg-[#1E293B] text-white text-sm font-semibold hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {busy === p.id && <Loader2 size={16} className="animate-spin" />}
                {proLabel(p)}
              </button>
            )}
          </div>
        ))}
      </div>
      <FirmPlans />
    </div>
  );
}
